import { Fragment, type ReactNode } from "react";
import { Streamdown } from "streamdown";

import { SlideChip } from "./SlideChip.tsx";

const SLIDE_RE = /\bslide\s+(\d{1,3})\b/gi;

// Post-process rendered text nodes so "slide N" becomes an interactive chip.
// Streamdown streams tokens; we intercept text through the components map on
// text-bearing elements (p, li) so the transform survives partial parses.
const linkifySlides = (children: ReactNode): ReactNode => {
  if (typeof children === "string") {
    const out: ReactNode[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    SLIDE_RE.lastIndex = 0;
    while ((m = SLIDE_RE.exec(children)) !== null) {
      if (m.index > last) out.push(children.slice(last, m.index));
      const n = Number(m[1]);
      if (Number.isFinite(n) && n >= 1 && n <= 200) {
        out.push(<SlideChip key={`${m.index}-${n}`} label={m[0]} slide={n - 1} />);
      } else {
        out.push(m[0]);
      }
      last = m.index + m[0].length;
    }
    if (last < children.length) out.push(children.slice(last));
    return out.length === 1
      ? out[0]
      : (
          <>
            {out.map((c, i) => (
              <Fragment key={i}>{c}</Fragment>
            ))}
          </>
        );
  }
  if (Array.isArray(children)) {
    return children.map((c, i) => <Fragment key={i}>{linkifySlides(c)}</Fragment>);
  }
  return children;
};

export function Markdown({ children }: { children: string }) {
  return (
    <div className="text-sm leading-relaxed [&_p]:my-1.5 [&_h1]:mt-3 [&_h1]:mb-1.5 [&_h1]:text-base [&_h1]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1.5 [&_h2]:text-sm [&_h2]:font-semibold [&_h3]:mt-2.5 [&_h3]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_ul]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-1.5 [&_ol]:list-decimal [&_ol]:list-inside [&_a]:text-[var(--color-primary)] [&_a]:underline [&_a]:underline-offset-2 [&_blockquote]:my-1.5 [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--color-border)] [&_blockquote]:pl-3 [&_blockquote]:text-[var(--color-muted-foreground)] [&_hr]:my-2 [&_hr]:border-[var(--color-border)] [&_code]:font-mono [&_code]:text-[12px] [&_code:not(pre_*)]:rounded [&_code:not(pre_*)]:bg-[var(--color-secondary)] [&_code:not(pre_*)]:px-1 [&_code:not(pre_*)]:py-0.5 [&_pre]:my-1.5 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:border-[var(--color-border)] [&_pre]:bg-[var(--color-secondary)] [&_pre]:p-2 [&_pre]:font-mono [&_pre]:text-[12px] [&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs [&_th]:border-b [&_th]:border-[var(--color-border)] [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold [&_td]:border-b [&_td]:border-[var(--color-border)] [&_td]:px-2 [&_td]:py-1">
      <Streamdown
        parseIncompleteMarkdown
        components={{
          p: ({ children }) => <p>{linkifySlides(children)}</p>,
          li: ({ children }) => <li>{linkifySlides(children)}</li>,
          a: ({ children, ...rest }) => (
            <a target="_blank" rel="noreferrer" {...rest}>
              {children}
            </a>
          ),
        }}
      >
        {children}
      </Streamdown>
    </div>
  );
}
